import { apiGet, apiPatch, apiPost } from './api';

export type JobParamSchema = {
  key: string;
  label: string;
  type: 'string' | 'number' | 'boolean';
  required?: boolean;
  default?: string | number | boolean;
};

export type JobDefinition = {
  jobId: string;
  name: string;
  description: string;
  category: string;
  defaultCron: string | null;
  defaultEnabled: boolean;
  supportsDryRun: boolean;
  paramSchema: JobParamSchema[];
  schedule: {
    enabled: boolean;
    cronExpression: string | null;
    timezone: string;
    lastScheduledAt?: string | null;
    updatedAt?: string;
  };
  isRunning: boolean;
  lastRun: {
    runId: string;
    status: string;
    startedAt: string;
    finishedAt?: string;
    durationMs?: number;
    trigger: string;
    dryRun?: boolean;
    errorMessage?: string | null;
  } | null;
};

export type JobRun = {
  _id: string;
  jobId: string;
  status: 'queued' | 'running' | 'success' | 'failed';
  trigger: 'scheduled' | 'manual';
  triggeredBy?: string | null;
  params: Record<string, unknown>;
  dryRun?: boolean;
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
  result?: unknown;
  error?: { message?: string; stack?: string };
  logs?: string[];
  createdAt: string;
};

export async function listJobs() {
  return apiGet<{
    success: boolean;
    data: { jobs: JobDefinition[]; schedulerEnabled: boolean; refreshedAt: string };
  }>('/api/v1/admin/jobs');
}

export async function listJobRuns(params?: {
  jobId?: string;
  status?: string;
  limit?: number;
  skip?: number;
}) {
  const q = new URLSearchParams();
  if (params?.jobId) q.set('jobId', params.jobId);
  if (params?.status) q.set('status', params.status);
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.skip) q.set('skip', String(params.skip));
  const query = q.toString();
  return apiGet<{
    success: boolean;
    data: { runs: JobRun[]; total: number; limit: number; skip: number };
  }>(`/api/v1/admin/jobs/runs${query ? `?${query}` : ''}`);
}

export async function getJobRun(runId: string) {
  return apiGet<{ success: boolean; data: JobRun }>(`/api/v1/admin/jobs/runs/${runId}`);
}

export async function triggerJob(
  jobId: string,
  body?: { params?: Record<string, unknown>; dryRun?: boolean }
) {
  return apiPost<{ success: boolean; data: JobRun; message?: string }>(
    `/api/v1/admin/jobs/${jobId}/run`,
    body || {}
  );
}

export async function updateJobSchedule(
  jobId: string,
  body: { enabled?: boolean; cronExpression?: string | null; timezone?: string }
) {
  return apiPatch<{ success: boolean; data: unknown; message?: string }>(
    `/api/v1/admin/jobs/${jobId}/schedule`,
    body
  );
}

export function formatDurationMs(ms?: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
}

export function statusColor(
  status: string
): 'default' | 'success' | 'error' | 'warning' | 'info' {
  if (status === 'success') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'running') return 'info';
  return 'default';
}
